import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getAuthCookieInfo } from 'utils/functions/authentication';
import { status } from 'constants/general';
import {
  fetchSearchProducts,
  resetSearchProducts,
  fetchSubmitOrderBulk,
} from 'modules/order/actions';
import {
  addBulkOrderFormStepsEnum,
  paymentMethodEnum,
  orderSourceEnum,
  attachmentTypeEnum,
} from 'modules/order/enums';
import BulkOrderComponent from 'modules/order/components/BulkOrderComponent';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const BulkOrderContainer = () => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const { getAddressInfo, loginInfo, firebaseConfig } = useSelector(state => ({
    getAddressInfo: state.order.getAddressInfo,
    loginInfo: state.accountManagement.loginInfo,
    firebaseConfig: state.common.firebaseConfig,
  }));

  const dispatch = useDispatch();
  const navigate = useNavigate();
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [currentStep, setCurrentStep] = useState(addBulkOrderFormStepsEnum.orderDetails);
  const [orders, setOrders] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [formFieldsValues, setFormFieldsValues] = useState({
    name: '',
    promocode: '',
  });
  const [formFieldsValidation, setFormFieldsValidation] = useState({
    name: true,
    promocode: true,
    orders: true,
  });
  const [selectedAddress, setSelectedAddress] = useState({});
  const [isAddPatientPopupOpen, setIsAddPatientPopupOpen] = useState(false);
  const [isPreviewFilePopupOpen, setIsPreviewFilePopupOpen] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState('');
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [patientIndexToEdit, setPatientIndexToEdit] = useState(0);
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  useEffect(() => {
    if (getAddressInfo.status === status.SUCCESS) setSelectedAddress(getAddressInfo.details[0]);
  }, [getAddressInfo]);
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  const getSearchProductsResults = query => {
    const body = {
      data: {
        query,
        from: 1,
        size: 5,
        isTrending: false,
        Version: 2,
      },
    };

    dispatch(fetchSearchProducts(body));
  };

  const resetSearchProductsResults = () => {
    dispatch(resetSearchProducts());
  };
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleTextInputFieldChange = (fieldName, fieldEvent) => {
    const { value } = fieldEvent.target;

    setFormFieldsValues(prevValue => {
      const fieldsObj = { ...prevValue };
      fieldsObj[fieldName] = value;

      return fieldsObj;
    });
  };

  const handleAddPatientBtnClick = () => {
    setIsAddPatientPopupOpen(true);
  };

  const handlePopUpConfirmBtnClick = () => {
    let isFormValid = true;

    if (!formFieldsValues.name) {
      setFormFieldsValidation({
        name: !!formFieldsValues.name,
      });
      isFormValid = false;
    }

    if (isFormValid === false) return;

    setFormFieldsValidation({
      name: true,
    });

    setIsAddPatientPopupOpen(false);

    if (isInEditMode) {
      const updatedOrders = [...orders];
      updatedOrders[patientIndexToEdit].patientName = formFieldsValues.name;
      setOrders(updatedOrders);

      setFormFieldsValues(prevState => ({
        ...prevState,
        name: '',
      }));

      setIsInEditMode(false);
      setPatientIndexToEdit(0);
    } else {
      const updatedOrders = [...orders];
      updatedOrders.push({
        patientName: formFieldsValues.name,
        orderDetails: [],
      });
      setOrders(updatedOrders);

      setFormFieldsValues(prevState => ({
        ...prevState,
        name: '',
      }));
    }
  };

  const handlePopUpCloseBtnClick = () => {
    setIsAddPatientPopupOpen(false);
    setIsInEditMode(false);
    setPatientIndexToEdit(0);

    setFormFieldsValues(prevState => ({
      ...prevState,
      name: '',
    }));
    setFormFieldsValidation(prevState => ({
      ...prevState,
      name: true,
    }));
  };

  const handleEditPatientNameClick = (patientName, index) => {
    setFormFieldsValues(prevState => ({
      ...prevState,
      name: patientName,
    }));

    setIsAddPatientPopupOpen(true);
    setIsInEditMode(true);
    setPatientIndexToEdit(index);
  };

  const handleDeletePatientIconClick = index => {
    const updatedOrders = [...orders];
    updatedOrders.splice(index, 1);
    setOrders(updatedOrders);
  };

  const handleProductItemClick = (product, index, searchProductItemClickFunc) => {
    dispatch(resetSearchProducts());
    searchProductItemClickFunc();

    const updatedOrders = [...orders];

    if (
      updatedOrders[index].orderDetails.some(item => item.key === product.productKey) === false &&
      product.stockQuantity > 0
    )
      updatedOrders[index].orderDetails.push({
        id: product.id,
        key: product.productKey,
        name: product.productNameEn,
        type: product.productShapeTypeName,
        price: `${product.newPrice} ${product.currencyEn}`,
        actualPrice: product.newPrice,
        currency: product.currencyEn,
        image: product.productShapeIconUrl,
        quantity: 1,
      });

    setOrders(updatedOrders);
    setTotalPrice(prevState => Math.round((prevState + product.newPrice) * 100) / 100);
  };

  const handleProductItemQuantityIconClick = (
    changeAmount,
    updatedAmount,
    index,
    detailsIndex,
    shouldRemoveItem,
  ) => {
    const updatedOrders = [...orders];

    if (shouldRemoveItem) {
      const itemPrice = updatedOrders[index].orderDetails[detailsIndex].actualPrice;
      updatedOrders[index].orderDetails.splice(detailsIndex, 1);

      setTotalPrice(prevState => Math.round((prevState - itemPrice) * 100) / 100);
    } else {
      const currentItem = updatedOrders[index].orderDetails[detailsIndex];
      updatedOrders[index].orderDetails[detailsIndex] = {
        ...currentItem,
        quantity: updatedAmount,
        price: `${currentItem.actualPrice * updatedAmount} ${currentItem.currency}`,
      };

      setTotalPrice(
        prevState => Math.round((prevState + currentItem.actualPrice * changeAmount) * 100) / 100,
      );
    }

    setOrders(updatedOrders);
  };

  const handleUploadImage = (uploadedFile, index) => {
    const { shouldBeProcessed, isProcessed } = uploadedFile;

    if (shouldBeProcessed && isProcessed === false) return;

    const updatedOrders = [...orders];
    updatedOrders[index].orderDetails = [
      ...updatedOrders[index].orderDetails,
      {
        key: new Date().getTime(),
        isFile: true,
        ...uploadedFile,
      },
    ];

    setOrders(updatedOrders);
  };

  const handlePreviewImageClick = (openPopup, file) => {
    if (openPopup) {
      setPreviewFileUrl(file.dataUrl);
      setIsPreviewFilePopupOpen(true);
    } else setIsPreviewFilePopupOpen(false);
  };

  const handleDeleteImageIconClick = (event, index, detailsIndex) => {
    if (event) event.stopPropagation();

    const updatedOrders = [...orders];
    updatedOrders[index].orderDetails.splice(detailsIndex, 1);

    setOrders(updatedOrders);
  };

  const handleAddressCardClick = address => {
    setSelectedAddress(address);
  };

  const handleAddAddressBtnClick = () => {
    navigate('/addAddress', { replace: true });
  };

  const handleSubmitBtnClick = () => {
    if (currentStep === addBulkOrderFormStepsEnum.orderDetails) {
      let isPromocodeValid = true;
      let isOrdersValid = true;
      let isFormValid = true;

      if (!formFieldsValues.promocode) {
        isPromocodeValid = false;
        isFormValid = false;
      }

      if (!orders.length) {
        isOrdersValid = false;
        isFormValid = false;
      }

      setFormFieldsValidation(prevState => ({
        ...prevState,
        promocode: isPromocodeValid,
        orders: isOrdersValid,
      }));

      if (isFormValid === false) return;

      setFormFieldsValidation(prevState => ({
        ...prevState,
        orders: true,
      }));

      setCurrentStep(addBulkOrderFormStepsEnum.addressDetails);
      return;
    }

    let ordersFiles = [];
    orders.forEach((order, index) => {
      const orderFiles = order.orderDetails
        .filter(item => item.isFile)
        .map((item, detailsIndex) => ({
          index,
          detailsIndex,
          fileData: item.fileData,
        }));
      ordersFiles = [...ordersFiles, ...orderFiles];
    });

    const { authToken } = getAuthCookieInfo();

    const orderChunkSizeValue =
      firebaseConfig.details[process.env.REACT_APP_FIREBASE_ORDER_CHUNK_SIZE] ||
      process.env.REACT_APP_ORDER_CHUNK_SIZE_FALLBACK;
    const orderPauseTimeValue =
      firebaseConfig.details[process.env.REACT_APP_FIREBASE_ORDER_PAUSE_TIME] ||
      process.env.REACT_APP_ORDER_PAUSE_TIME_FALLBACK;

    const body = {
      headers: {
        Authorization: `Bearer ${authToken}`,
        'x-vzt-token': loginInfo.details.AccessToken,
        'accept-language': 'en-EG',
      },
      data: orders.map(order => ({
        userKey: loginInfo.details.UserKey,
        addressKey: selectedAddress.key,
        source: orderSourceEnum.bulkOrder,
        paymentMethodKey: paymentMethodEnum.cash,
        loyaltyProgramEnabled: false,
        promoCode: formFieldsValues.promocode,
        note: '',
        patientDetails: {
          Name: order.patientName,
          Age: 30,
          Gender: 1,
        },
        orderItems: order.orderDetails
          .filter(item => !item.isFile)
          .map(item => ({
            userKey: loginInfo.details.UserKey,
            quantity: item.quantity,
            productKey: item.key,
            productShapeId: item.id,
            note: '',
          })),
        orderAttachments: order.orderDetails
          .filter(item => item.isFile)
          .map(() => ({
            userKey: loginInfo.details.UserKey,
            attachmentType: attachmentTypeEnum.image,
            note: '',
          })),
      })),
      ordersFiles,
      bulkOrderConfigurations: {
        orderChunkSizeValue,
        orderPauseTimeValue,
      },
    };

    dispatch(fetchSubmitOrderBulk(body));
  };

  const handleBackBtnClick = () => {
    setCurrentStep(addBulkOrderFormStepsEnum.orderDetails);
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  return (
    <BulkOrderComponent
      currentStep={currentStep}
      orders={orders}
      totalPrice={totalPrice}
      formFieldsValues={formFieldsValues}
      formFieldsValidation={formFieldsValidation}
      selectedAddress={selectedAddress}
      isAddPatientPopupOpen={isAddPatientPopupOpen}
      isPreviewFilePopupOpen={isPreviewFilePopupOpen}
      previewFileUrl={previewFileUrl}
      isInEditMode={isInEditMode}
      getSearchProductsResults={getSearchProductsResults}
      resetSearchProductsResults={resetSearchProductsResults}
      handleTextInputFieldChange={handleTextInputFieldChange}
      handleAddPatientBtnClick={handleAddPatientBtnClick}
      handlePopUpConfirmBtnClick={handlePopUpConfirmBtnClick}
      handlePopUpCloseBtnClick={handlePopUpCloseBtnClick}
      handleEditPatientNameClick={handleEditPatientNameClick}
      handleDeletePatientIconClick={handleDeletePatientIconClick}
      handleProductItemClick={handleProductItemClick}
      handleProductItemQuantityIconClick={handleProductItemQuantityIconClick}
      handleUploadImage={handleUploadImage}
      handlePreviewImageClick={handlePreviewImageClick}
      handleDeleteImageIconClick={handleDeleteImageIconClick}
      handleAddressCardClick={handleAddressCardClick}
      handleAddAddressBtnClick={handleAddAddressBtnClick}
      handleSubmitBtnClick={handleSubmitBtnClick}
      handleBackBtnClick={handleBackBtnClick}
    />
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

BulkOrderContainer.propTypes = {};

BulkOrderContainer.defaultProps = {};

export default BulkOrderContainer;
